// Shared, ref-counted pusher-js connections.
//
// pusher-js is built to hold ONE WebSocket and multiplex EVERY channel
// subscription over it. Constructing a `new Pusher()` per hook call (the shape
// before this module) opened a separate socket per subscribing component — the
// workspace alone opened TWO to the same `private-buyer-{id}` channel — so the
// concurrent-connection count (the dimension Pusher bills and caps on) scaled
// with mounted components, not users.
//
// This module keeps ONE `Pusher` per distinct connection config and ref-counts
// channel subscriptions on it: a user holds a single socket no matter how many
// `usePusherChannel` consumers mount, and each channel is `subscribe`d once and
// `unsubscribe`d only when its LAST consumer leaves. The idle-tab visibility
// disconnect lives here too — ONE handler per connection, not per hook.
//
// INTERNAL to @recruiter/shared. Apps reach this ONLY through usePusherChannel /
// usePusherHold (this module is deliberately absent from src/index.ts, the
// deep-import lint ban blocks the side door, and importing pusher-js anywhere
// else is lint-banned too). Manual acquire/release pairing is exactly the kind
// of bookkeeping that breeds leaks — the hooks tie both to the React effect
// lifecycle so a consumer CANNOT forget the release half.

import Pusher from "pusher-js";

import type { PusherChannelConfig } from "./usePusherChannel";

/** Grace before a hidden tab releases its socket (see PusherChannelConfig). */
export const DEFAULT_IDLE_DISCONNECT_MS = 30_000;

/**
 * A Pusher connection-health signal. `kind` is the sub-kind
 * (`subscription_error` | `idle_disconnect` | `reopen` | `refcount_underflow`);
 * `detail` is optional free context (a channel name, a timing). RUM consumes these
 * as client errors (kind="pusher").
 */
export interface PusherHealthEvent {
  kind: string;
  detail?: string;
}

// Inverted dependency: the pusher manager NEVER imports rum. Instead rum (or any
// consumer) registers a callback here, and the health sites below emit into it.
// This keeps pusher/ app-agnostic — it emits health signals whether or not anyone
// is listening. One handler process-wide (module singleton), like the connections.
let pusherHealthHandler: ((event: PusherHealthEvent) => void) | null = null;

/**
 * Register (or clear, with `null`) the consumer of Pusher connection-health
 * signals. The app's `initRum` wires this to RUM's client-error queue; nothing
 * else should. Absent a handler, `emitPusherHealth` is a silent no-op.
 */
export function setPusherHealthHandler(handler: ((event: PusherHealthEvent) => void) | null): void {
  pusherHealthHandler = handler;
}

/** Emit a health signal to the registered consumer (no-op when none). INTERNAL. */
export function emitPusherHealth(event: PusherHealthEvent): void {
  pusherHealthHandler?.(event);
}

type PusherChannel = ReturnType<Pusher["subscribe"]>;

interface ChannelEntry {
  channel: PusherChannel;
  /** Consumers currently bound to this channel on the shared connection. */
  refCount: number;
}

interface ConnectionEntry {
  pusher: Pusher;
  channels: Map<string, ChannelEntry>;
  /** Consumers across ALL channels on this connection. */
  refCount: number;
  /** The idle policy this connection runs (first consumer wins — see acquireChannel). */
  idleMs: number;
  visibilityEnabled: boolean;
  onVisibility: (() => void) | null;
  hideTimer: ReturnType<typeof setTimeout> | null;
  disconnectedByVisibility: boolean;
  /** Arm the idle-drop timer if (and only if) the tab is hidden and nothing holds. */
  armDropIfIdle: () => void;
  /** Cancel a pending (not yet fired) idle drop. */
  cancelPendingDrop: () => void;
}

export interface ChannelLease {
  /** The shared pusher-js channel — bind/unbind event handlers on it. */
  channel: PusherChannel;
  /**
   * True when the channel was ALREADY subscribed at lease time (a late joiner to
   * a live channel). Such a consumer's own bootstrap holds current state, so its
   * next `pusher:subscription_succeeded` is a RECONNECT, not the initial subscribe
   * — the hook seeds `subscribedOnce` from this so its reconcile fires correctly.
   */
  wasAlreadySubscribed: boolean;
  /** Drop this consumer: unsubscribe the channel / disconnect the socket if last. */
  release: () => void;
}

// One entry per distinct SOCKET identity (see connectionKey). Module-level so all
// `usePusherChannel` consumers — across web/seller/admin — that share a config
// share a socket.
const connections = new Map<string, ConnectionEntry>();

// Visibility HOLDS, keyed like connections. While a connection has ≥1 hold, the
// idle-tab drop never arms (and a pending drop is cancelled): the app is awaiting
// server events that must reach the user even in a hidden tab — an in-flight
// agent turn, a concierge run pinging "a designer has a question" via
// notification/favicon, an export waiting for its ready event. Held in their own
// map (not on the entry) so a hold can be taken before/without a live connection
// and survives connection teardown/rebuild.
const holdCounts = new Map<string, number>();

// The socket identity: everything that decides WHICH server/identity the WebSocket
// talks to. NOT idleDisconnectMs — that is a per-connection visibility policy taken
// from the first consumer, so differing values never fragment a user's socket (each
// app passes one config, so it is consistent in practice anyway).
function connectionKey(config: PusherChannelConfig): string {
  return JSON.stringify({
    key: config.key,
    host: config.host,
    port: config.port,
    useTls: config.useTls,
    cluster: config.cluster ?? null,
    authEndpoint: config.authEndpoint,
    authHeaders: config.authHeaders ?? {},
  });
}

function createConnection(key: string, config: PusherChannelConfig): ConnectionEntry {
  const pusher = new Pusher(config.key, {
    wsHost: config.host,
    wsPort: config.port,
    wssPort: config.port,
    forceTLS: config.useTls,
    enabledTransports: ["ws", "wss"],
    disableStats: true,
    cluster: config.cluster ?? "mt1",
    channelAuthorization: {
      endpoint: config.authEndpoint,
      transport: "ajax",
      headers: config.authHeaders ?? {},
    },
  });

  // M7 — browser-side connection-health reporting. pusher-js drives ONE socket per
  // connection through a small state machine; bind its `state_change` so a drop /
  // recovery becomes a health signal (→ RUM, kind="pusher"). We report only the
  // transitions that matter operationally — connected / disconnected / unavailable /
  // reconnecting — and only ON a transition (state_change fires once per change, so
  // a stable socket emits nothing: no flapping noise). `detail` carries prev->new.
  //
  // pusher-js states: initialized → connecting → connected, plus unavailable (network
  // down / server unreachable) and disconnected. A `connecting` that follows anything
  // past the initial handshake is a RECONNECT attempt (surfaced as "reconnecting");
  // the very first connect is not reported (it's the expected startup path).
  pusher.connection.bind("state_change", (states: { previous: string; current: string }): void => {
    const { previous, current } = states;
    let kind: string | null = null;
    if (current === "connected") kind = "connected";
    else if (current === "unavailable") kind = "unavailable";
    else if (current === "disconnected") kind = "disconnected";
    else if (current === "connecting" && previous !== "initialized" && previous !== "connecting")
      kind = "reconnecting";
    if (kind !== null) emitPusherHealth({ kind, detail: `${previous}->${current}` });
  });

  const idleMs = config.idleDisconnectMs ?? DEFAULT_IDLE_DISCONNECT_MS;
  const entry: ConnectionEntry = {
    pusher,
    channels: new Map(),
    refCount: 0,
    idleMs,
    visibilityEnabled: idleMs > 0 && typeof document !== "undefined",
    onVisibility: null,
    hideTimer: null,
    disconnectedByVisibility: false,
    armDropIfIdle: (): void => {
      // Debounce: only a tab hidden past the grace releases the socket, so an
      // alt-tab peek is a no-op. Arm only when the tab is actually hidden, once
      // at least one channel has completed its initial subscribe (never
      // mid-handshake — dropping then would make the post-show subscribe look
      // initial and suppress the reconcile), never while a HOLD is active (the
      // app is awaiting events that must reach a hidden tab — notifications,
      // export-ready, an in-flight turn), and never double-arm or re-arm an
      // already-dropped socket.
      if (!entry.visibilityEnabled || !document.hidden) return;
      if (entry.hideTimer || entry.disconnectedByVisibility) return;
      if ((holdCounts.get(key) ?? 0) > 0) return;
      const anySubscribed = [...entry.channels.values()].some((c) => c.channel.subscribed === true);
      if (!anySubscribed) return;
      entry.hideTimer = setTimeout(() => {
        entry.hideTimer = null;
        // A hold may have been taken between arming and firing — re-check.
        if ((holdCounts.get(key) ?? 0) > 0) return;
        entry.disconnectedByVisibility = true;
        // Breadcrumb (doctrine 1.5 — loud in logs): if a flow ever misses an
        // event because it forgot to declare a hold, this line in the console is
        // the diagnosis.
        console.info(
          `[pusher-connection] idle disconnect: tab hidden >${idleMs}ms with no active hold: socket released; will reconnect + reconcile on return`,
        );
        emitPusherHealth({ kind: "idle_disconnect", detail: `hidden>${idleMs}ms` });
        pusher.disconnect();
      }, idleMs);
    },
    cancelPendingDrop: (): void => {
      if (entry.hideTimer) {
        clearTimeout(entry.hideTimer);
        entry.hideTimer = null;
      }
    },
  };

  if (entry.visibilityEnabled) {
    // Release the socket when the tab goes idle/backgrounded and reopen it on
    // return — the direct fix for idle tabs pinning a connection they aren't using.
    // Drives the SAME instance (disconnect()/connect()), NEVER a teardown: each
    // channel stays in pusher-js's map, so connect() re-subscribes them and
    // re-fires `pusher:subscription_succeeded`, which every bound consumer turns
    // into its `onReconnect` REST reconcile — identical to a network-drop recovery.
    entry.onVisibility = (): void => {
      if (document.hidden) {
        entry.armDropIfIdle();
      } else {
        entry.cancelPendingDrop();
        // Only reopen a socket WE dropped, so a re-show racing pusher-js's own
        // reconnect never double-drives the connection.
        if (entry.disconnectedByVisibility) {
          entry.disconnectedByVisibility = false;
          console.info(
            "[pusher-connection] tab visible again: reopening socket (re-subscribe + reconcile)",
          );
          emitPusherHealth({ kind: "reopen" });
          pusher.connect();
        }
      }
    };
    document.addEventListener("visibilitychange", entry.onVisibility);
  }

  return entry;
}

function teardownConnection(key: string, entry: ConnectionEntry): void {
  if (entry.visibilityEnabled && entry.onVisibility) {
    document.removeEventListener("visibilitychange", entry.onVisibility);
  }
  if (entry.hideTimer) {
    clearTimeout(entry.hideTimer);
    entry.hideTimer = null;
  }
  entry.pusher.disconnect();
  connections.delete(key);
}

/**
 * Join `channelName` on the shared connection for `config`, creating the socket
 * and/or the channel subscription on first use and ref-counting both. Call
 * `release()` on unmount; the channel is unsubscribed when its last consumer
 * leaves and the socket disconnected when its last consumer (across all channels)
 * leaves.
 */
export function acquireChannel(config: PusherChannelConfig, channelName: string): ChannelLease {
  const key = connectionKey(config);
  let conn = connections.get(key);
  if (!conn) {
    conn = createConnection(key, config);
    connections.set(key, conn);
  }
  // The idle policy is per-CONNECTION, first consumer wins (the key deliberately
  // excludes it so differing values can never fragment a user's socket). A
  // consumer passing a different value would silently get the live policy —
  // surface the disagreement instead of letting someone chase a "wrong timeout".
  const requestedIdleMs = config.idleDisconnectMs ?? DEFAULT_IDLE_DISCONNECT_MS;
  if (requestedIdleMs !== conn.idleMs) {
    console.warn(
      `[pusher-connection] conflicting idleDisconnectMs for one connection: this consumer asked for ${requestedIdleMs}ms but the live socket runs ${conn.idleMs}ms (first consumer wins). Align the app's PusherChannelConfig.`,
    );
  }
  conn.refCount += 1;

  let chEntry = conn.channels.get(channelName);
  if (!chEntry) {
    chEntry = { channel: conn.pusher.subscribe(channelName), refCount: 0 };
    conn.channels.set(channelName, chEntry);
  }
  chEntry.refCount += 1;
  const wasAlreadySubscribed = chEntry.channel.subscribed === true;

  const boundConn = conn;
  const boundChannel = chEntry;
  let released = false;
  return {
    channel: boundChannel.channel,
    wasAlreadySubscribed,
    release(): void {
      if (released) return;
      released = true;
      boundChannel.refCount -= 1;
      boundConn.refCount -= 1;
      // Underflow is unreachable through the hooks (each lease releases once,
      // enforced by the flag above) — guard anyway and scream (doctrine 1.5):
      // a negative count means the bookkeeping is broken, and clamping keeps
      // the failure from cascading into tearing down a socket still in use.
      if (boundChannel.refCount < 0 || boundConn.refCount < 0) {
        console.error(
          `[pusher-connection] refcount underflow (channel=${String(boundChannel.refCount)}, connection=${String(boundConn.refCount)}), a lease was released more than once`,
        );
        emitPusherHealth({
          kind: "refcount_underflow",
          detail: `channel=${String(boundChannel.refCount)},connection=${String(boundConn.refCount)}`,
        });
        boundChannel.refCount = Math.max(0, boundChannel.refCount);
        boundConn.refCount = Math.max(0, boundConn.refCount);
      }
      if (boundChannel.refCount === 0 && boundConn.channels.get(channelName) === boundChannel) {
        boundConn.pusher.unsubscribe(channelName);
        boundConn.channels.delete(channelName);
      }
      if (boundConn.refCount === 0 && connections.get(key) === boundConn) {
        teardownConnection(key, boundConn);
      }
    },
  };
}

/**
 * Suspend the idle-tab disconnect on the shared connection for `config` while the
 * app is awaiting server events that must reach the user EVEN IN A HIDDEN TAB —
 * an in-flight agent turn, a concierge run that pings "a designer has a question"
 * via browser notification / favicon dot, an export waiting for its ready event.
 * While ≥1 hold is active the hidden-tab drop never arms (and a pending drop is
 * cancelled); releasing the last hold while the tab is hidden arms the drop as if
 * the tab had just been hidden. Returns the release function; releasing twice is
 * a no-op. This is what keeps the idle-disconnect a pure resource optimization
 * with zero user-visible behavior change: only tabs with NOTHING awaited drop.
 */
export function acquireVisibilityHold(config: PusherChannelConfig): () => void {
  const key = connectionKey(config);
  holdCounts.set(key, (holdCounts.get(key) ?? 0) + 1);
  connections.get(key)?.cancelPendingDrop();
  let released = false;
  return (): void => {
    if (released) return;
    released = true;
    const next = (holdCounts.get(key) ?? 1) - 1;
    if (next > 0) {
      holdCounts.set(key, next);
      return;
    }
    holdCounts.delete(key);
    // The wait ended while the tab was already hidden (e.g. the turn completed
    // while the user reads email): start the idle clock now.
    connections.get(key)?.armDropIfIdle();
  };
}

/** Test-only: drop all shared connections (removes visibility listeners + timers). */
export function __resetPusherConnections(): void {
  for (const [key, entry] of [...connections.entries()]) {
    teardownConnection(key, entry);
  }
  connections.clear();
  holdCounts.clear();
}

/** Test-only: observe the bookkeeping so leak tests can assert "everything released". */
export function __pusherDebugCounts(): {
  connections: number;
  channelRefs: number;
  holds: number;
} {
  let channelRefs = 0;
  for (const conn of connections.values()) {
    for (const ch of conn.channels.values()) channelRefs += ch.refCount;
  }
  let holds = 0;
  for (const n of holdCounts.values()) holds += n;
  return { connections: connections.size, channelRefs, holds };
}
